import z from 'zod';

/**
 * Represents any valid JSON primitive value.
 */
export type JSONPrimitive = string | number | boolean | null;

/**
 * Represents any valid JSON value (recursive).
 */
export type JSONValue = JSONPrimitive | JSONObject | JSONArray;

/**
 * A JSON object with string keys and JSON values.
 */
export interface JSONObject {
  [key: string]: JSONValue;
}

/**
 * A JSON array of JSON values.
 */
export type JSONArray = JSONValue[];

/**
 * JSONB type for use with Drizzle `$type<JSONB>()` annotations.
 * Supports both objects and arrays at the top level.
 * For entity-specific JSONB columns, prefer domain types (e.g. CustomRibbonFilters).
 */
export type JSONB = JSONObject | JSONArray;

/**
 * Zod schema for validating JSONB values (arrays or objects at the top level).
 * Nested values are validated recursively against valid JSON types.
 */
const jsonValueSchema: z.ZodType<JSONValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number().finite(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ])
);

export const jsonbSchema: z.ZodType<JSONB> = z.union([
  z.array(jsonValueSchema),
  z.record(z.string(), jsonValueSchema),
]);

/**
 * Allowed value types for a single custom attribute field.
 *
 * - `string` — Text, Textarea, Date, Link, Select
 * - `number` — supported by external API
 * - `boolean` — supported by external API
 * - `string[]` — MultiSelect, UserMultiSelect, DepartmentMultiSelect, Tags
 * - `null` — unset/empty field
 */
export type CustomAttributeValue = string | number | boolean | string[] | null;

/**
 * Custom attribute data stored on entities. A flat key-value map where keys
 * are custom field identifiers (e.g. `controlType__fieldName`) and values
 * are user-entered data constrained to {@link CustomAttributeValue}.
 */
export type CustomAttributeData = Record<string, CustomAttributeValue>;

const customAttributeValueSchema = z.union([
  z.string(),
  z.number().finite(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);

/**
 * Zod schema for CustomAttributeData. Validates a string-keyed object with
 * values restricted to the supported custom field value types.
 *
 * Keys whose value is `undefined` are stripped before validation: an unset
 * custom-attribute field is semantically "not provided". Forms submit blank
 * fields as `undefined`, and unlike the legacy GraphQL path (where
 * `JSON.stringify` dropped them), the tRPC + superjson transport preserves
 * `undefined` over the wire — so they must be filtered here rather than
 * failing the value union (RSP-5940). Explicit `null` is preserved as a
 * deliberate "cleared" marker.
 */
export const customAttributeDataSchema: z.ZodType<
  CustomAttributeData,
  z.ZodTypeDef,
  unknown
> = z.preprocess(
  (val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      // Narrowed to a non-array object above; each value is validated below.
      const record = val as Record<string, unknown>;

      return Object.fromEntries(
        Object.entries(record).filter(([, value]) => value !== undefined)
      );
    }

    return val;
  },
  z.record(z.string(), customAttributeValueSchema)
);

export const auditFieldSchema = z.object({
  createdByUser: z.string(),
  modifiedByUser: z.string(),
  createdAtTimestamp: z.date(), // todo should these be strings? node is a bit inconsistent with date handling
  modifiedAtTimestamp: z.date(),
});

/**
 * Optimistic-concurrency lock token carried by every V3 update/delete
 * surface. Must be a full ISO 8601 datetime; `offset: true` accepts both
 * the drizzle customType emission (`2026-07-29T09:44:54.543829Z`) and the
 * Hasura/Apollo leg (`2026-07-29T09:44:54.543829+00:00`). No `precision`
 * option, so second / milli / microsecond fractions all pass.
 *
 * Format-validating here (V2 parity) turns a malformed token into a 400 at
 * the schema boundary instead of a Postgres `22007 invalid input syntax`
 * 500 inside `optimisticLockWhere` (COI-668).
 *
 * Which wrapper a given update surface uses is a pinned rule (COI-673):
 * the token is `originalTimestampNullishSchema` iff
 *
 *   (a) the entity's update is reachable through change-request replay and
 *       stored request blobs may predate the token — the merge consumer
 *       replays the persisted payload verbatim, so a blob written before
 *       the field existed must still apply (RSP-5853); or
 *   (b) the entity's V2 input declared the token nullable (e.g.
 *       `UpdateTestResultInput.OriginalTimestamp: timestamptz` — no `!`),
 *       so requiring it would reject payloads V2 accepted; or
 *   (c) the site was already nullish for an entity whose V2 surface had no
 *       token at all — grandfathered, because tightening it would break
 *       existing token-less callers.
 *
 * Otherwise the token is required (`originalTimestampSchema`): V2-required
 * parity sites, and entities whose lock V3 introduced (COI-624) where every
 * caller has always sent it. The classification is enforced per-procedure by
 * `packages/trpc/src/routers/original-timestamp-classification.test.ts` —
 * changing a site's wrapper without reclassifying it there is a test
 * failure.
 */
export const originalTimestampSchema = z.string().datetime({
  offset: true,
  message: 'OriginalTimestamp must be an ISO 8601 datetime',
});

/**
 * Nullish variant for surfaces where the lock token may be legitimately
 * omitted — CR-replay-reachable entities, V2-nullable inputs, and
 * grandfathered relaxations (see the rule on `originalTimestampSchema`). An
 * omitted token makes the data-layer skip the lock; explicit null represents
 * a caller snapshot of a never-modified row and locks on
 * `ModifiedAtTimestamp IS NULL` (`optimisticLockWhere`).
 */
export const originalTimestampNullishSchema = originalTimestampSchema.nullish();

/**
 * Rating band for `document_assessment_result` and
 * `obligation_assessment_result` — and ONLY those two tables.
 *
 * Both carry a validated `rating_check` CHECK constraint restricting Rating to
 * 1–5, added by the squashed baseline migration
 * (`1709744128112_squashed/up.sql:26876` and `:26951`, plain `ADD CONSTRAINT`
 * with no `NOT VALID`). So 0 and anything above 5 has never been storable here
 * on either V2 or V3: V2 accepted it at the schema boundary and then took a
 * Postgres constraint violation. The 1–5 bound is therefore a faithful mirror
 * of the DB, not a V3 narrowing (COI-650), and it turns that 500 into a 400.
 *
 * Deliberately NOT applied to the internal-audit / oversight siblings, even
 * though they hold the same kind of value. Those tables are **unconstrained**:
 * `{document,obligation}_internal_audit_result` are created with a plain
 * `"Rating" INTEGER` (`1750675590556_segregate-risk-ratings-to-ia/up.sql:508`,
 * `:628`), and the oversight pair was renamed from the second-line tables
 * created the same way (`1750849742690_segregate-risk-ratings-to-second-line`,
 * which contains no CHECK at all). Bounding them would reject writes V2
 * accepted AND stored — the exact breakage COI-650 set out to prevent.
 *
 * Beware `packages/drizzle/src/schema.ts`: it declares `rating_check` on all
 * six tables. That file is a hand-maintained mirror (see
 * `packages/drizzle/CLAUDE.md`), not introspected output, and those four extra
 * checks do not exist in the database. Trust the migration DDL.
 *
 * Widening even this band is a product change: the CHECK constraints on the two
 * assessment tables have to be dropped by migration first.
 */
export const assessmentResultRatingSchema = z.number().int().min(1).max(5);

/**
 * Nullish variant of {@link assessmentResultRatingSchema} for partial `_set`
 * updates, where an absent Rating means "leave unchanged". Creates require the
 * value: V2 required it on every document/obligation result insert.
 */
export const assessmentResultRatingNullishSchema =
  assessmentResultRatingSchema.nullish();

/**
 * PostgreSQL `integer` (int4) range. Any value outside it is a
 * `22003 numeric value out of range` from the driver, so bounding an otherwise
 * unconstrained integer column here keeps that a 400 instead of a 500 — the
 * COI-668 rule. Not a product bound: it is the column's physical capacity.
 */
const PG_INT4_MIN = -2147483648;
const PG_INT4_MAX = 2147483647;

/**
 * Rating for the internal-audit / oversight document+obligation results, whose
 * tables carry no CHECK constraint (see
 * {@link assessmentResultRatingSchema} for the DDL cites).
 *
 * Required, not nullish: V2's four document and obligation internal-audit and
 * oversight result schemas all declared `Rating: z.number().int({ message:
 * 'Required' })` with no `nullable`, so a NULL-rated finding was rejected.
 *
 * Unbounded as a *rating*: no CHECK constrains the band, so any rating V2
 * accepted still persists — including 0 and values above 5, which an org's
 * internal-audit or oversight scale may legitimately use. The only bound is the
 * int4 range of the column itself, so an absurd value is a 400 here rather than
 * a Postgres out-of-range 500.
 */
export const unboundedResultRatingSchema = z
  .number()
  .int()
  .min(PG_INT4_MIN)
  .max(PG_INT4_MAX);
/**
 * Viewer-array input fragments for the V3 tRPC boundary (COI-675).
 *
 * The data-layer declares every viewer array `.optional()` — it has
 * no `.nullish()` counterpart anywhere (all 52 declarations were checked: 30
 * in `services/data-layer/src/schemas/`, 22 in the per-entity create/update
 * processors). An explicit `null` therefore fails data-layer validation, so
 * before this fragment a caller sending `ViewerUserIds: null` 400'd deep in
 * the data-layer instead of at the tRPC edge. Web hooks type these
 * `string[] | null`, so `null` is client-reachable — though the canonical form
 * helper emits `[]` rather than `null`, which is why this went unnoticed.
 *
 * The escape route that made it bite is the change-request blob, not the
 * direct-apply call. `executeAsyncRequest` passes the RAW input to `apiCall`
 * (`clients/async-request.ts:154`) — the services' `?? undefined` sits inside
 * `buildRequestBody`, which only feeds the request-state audit record
 * (`:135`), so direct-apply did carry the `null` to the data-layer. Worse, on
 * the approval-gated branch the same `null` reached `stripUndefinedJsonb`,
 * which strips `undefined` but KEEPS `null`, persisting `ViewerUserIds: null`
 * into `change_request.RequestedChanges` — where the merge replay hands it
 * back to the same `.optional()` schema and the CR can never be applied.
 * Collapsing here is what stops new blobs acquiring that shape.
 *
 * `.nullish()` keeps the inbound contract (callers may send `null`, omit the
 * key, or send an array); the transform collapses `null` to `undefined` so
 * only the three states the data-layer understands cross the wire. Those
 * states are the merged tri-state contract in
 * `services/data-layer/src/repositories/relationship-sync.ts`:
 * `undefined` = preserve, `[]` = clear, non-empty = exact set. Collapsing
 * `null` to `undefined` (= preserve) removes no clear path — clears travel
 * as `[]`.
 *
 * Normalising in the *input schema* rather than per-literal is deliberate:
 * only a minority of routers build an explicit object literal from
 * `req.input`; the rest forward it verbatim, where a `?? undefined` at the
 * call site has nothing to attach to. One declaration of the rule, reused
 * everywhere.
 */
export const viewerUserIdsSchema = z
  .array(z.string())
  .nullish()
  .transform((v) => v ?? undefined);

/** Group counterpart of `viewerUserIdsSchema` — see its rationale. */
export const viewerGroupIdsSchema = z
  .array(z.string().uuid())
  .nullish()
  .transform((v) => v ?? undefined);
